import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Plane, Ship } from "lucide-react";

/* Composant UNIQUE Mode de transport + Départ planifié (Phase 4).
   Utilisé à l'identique dans « J'ai déjà ma marchandise » et « Je veux acheter en ligne ».
   - [ Aérien / Express ]  [ Maritime ]
   - Les départs affichés correspondent EXACTEMENT à Hub + Destination + Mode
     (filtre backend GET /departures?country&dest_country&mode).
   - Changement de mode → l'ancien départ sélectionné est supprimé immédiatement
     et seuls les départs du nouveau mode sont rechargés.
   - DÉPART INFORMATIF ET NON BLOQUANT : le choix est facultatif ; sans départ planifié,
     le client continue normalement (attribution ultérieure par Colis2Go).
   - informative=true (« Je veux acheter en ligne ») : pas de choix — simple information
     « Prochain départ prévu : [date] » + affectation après réception au Hub. */
export const ModeDeparture = ({ hubCountry, destCountry, mode, onModeChange,
  departureId, onDepartureChange, testid = "md", informative = false }) => {
  const { t } = useI18n();
  const [deps, setDeps] = useState([]);
  useEffect(() => {
    if (hubCountry && destCountry && mode) {
      api.get(`/departures?country=${encodeURIComponent(hubCountry)}&dest_country=${encodeURIComponent(destCountry)}&mode=${mode}`)
        .then(({ data }) => setDeps(data)).catch(() => setDeps([]));
    } else setDeps([]);
  }, [hubCountry, destCountry, mode]);
  const pick = (m) => {
    if (m === mode) return;
    onModeChange(m);
    onDepartureChange("", null);
  };
  return (
    <div data-testid={`${testid}-modedep-box`}>
      <Label>{t("md.mode")}</Label>
      <div className="mt-1 flex gap-2">
        <Button type="button" variant={mode === "air" ? "default" : "outline"} data-testid={`${testid}-mode-air`}
          className={`rounded-sm flex-1 gap-1.5 ${mode === "air" ? "bg-primary text-white" : ""}`}
          onClick={() => pick("air")}><Plane className="h-4 w-4" /> {t("search.air")}</Button>
        <Button type="button" variant={mode === "sea" ? "default" : "outline"} data-testid={`${testid}-mode-sea`}
          className={`rounded-sm flex-1 gap-1.5 ${mode === "sea" ? "bg-primary text-white" : ""}`}
          onClick={() => pick("sea")}><Ship className="h-4 w-4" /> {t("search.sea")}</Button>
      </div>
      {mode && destCountry && (
        <div className="mt-2">
          {informative ? (
            deps.length > 0 ? (
              <div className="border border-border bg-secondary/60 px-3 py-2 text-sm rounded-sm" data-testid={`${testid}-nextdep`}>
                <span className="font-semibold">{t("md.nextDep")} :</span> {deps[0].departure_date}
                <div className="mt-0.5 text-xs text-muted-foreground">{t("md.assignAfter")}</div>
              </div>
            ) : (
              <div className="border border-border bg-secondary/60 px-3 py-2 text-xs text-muted-foreground rounded-sm"
                data-testid={`${testid}-nodep`}>{t("md.noDep")}</div>
            )
          ) : (
            <>
              <Label>{t("ssd.departure")}</Label>
              {deps.length > 0 ? (
                <>
                  <select data-testid={`${testid}-departure`} className="mt-1 w-full h-9 border border-border bg-card px-2 text-sm rounded-sm"
                    value={departureId} onChange={(e) => onDepartureChange(e.target.value, deps.find((d) => d.id === e.target.value) || null)}>
                    <option value="">{t("ssd.chooseDep")}</option>
                    {deps.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.departure_date} · {d.mode === "air" ? t("fp.air") : t("fp.sea")} → {d.dest_country} ({t("ssd.cutoff")} {d.cutoff_date})
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-muted-foreground" data-testid={`${testid}-indicative`}>{t("md.indicative")}</p>
                </>
              ) : (
                <div className="mt-1 min-h-9 flex items-center border border-border bg-secondary/60 px-3 py-2 text-sm text-muted-foreground rounded-sm"
                  data-testid={`${testid}-nodep`}>{t("md.noDep")}</div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
